import { Classes, Dialog, Popover } from "@blueprintjs/core";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanSubmitButtons } from "modules/spending/workload-plan/components/PlanSubmitButtons";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { PlanDraftApplyResult, PlanDraftPush, PlanDraftRange, PlanDraftView } from "../models/plan-draft";
import { ProjectPlanProject } from "../models/project-plan-types";
import { PlanConflict } from "../models/plan-conflicts";
import styles from "./PlanDraftPanel.module.scss";

interface PlanDraftPanelProps {
    busy: boolean;
    /** Есть несохранённые правки */
    dirty: boolean;
    /** На сервере есть сохранённый черновик */
    hasDraft: boolean;
    canUndo: boolean;
    /** Проекты графика — для подписей в списке изменений */
    projects: ProjectPlanProject[];
    /** Непогашенные конфликты плана с правками — предупреждение в окне применения */
    conflicts?: PlanConflict[];
    /** Черновик, посчитанный на странице, — что задел каскад по связям */
    preview?: PlanDraftView;
    /** Клик по сдвинутому каскадом объекту в списке — показать его на графике */
    onRevealPush?: (push: PlanDraftPush) => void;
    onSave: () => Promise<boolean>;
    onCancel: () => void;
    onUndo: () => void;
    onDiscard: () => void;
    /** Черновик, пересчитанный сервером, — что уйдёт в план */
    onReview: () => Promise<PlanDraftView | null>;
    /** «Применить черновик»; force — поверх чужих правок */
    onApply: (force: boolean) => Promise<PlanDraftApplyResult | null>;
}

const DAY = "DD.MM.YYYY";

/** Сколько конфликтов перечислять в окне применения — остальные в панели «Конфликты» */
const CONFLICTS_SHOWN = 8;

const date = (day: string | null) => (day ? moment(day, "YYYY-MM-DD").format(DAY) : "—");

const period = (ranges: PlanDraftRange[]) =>
    ranges.length ? `${date(ranges[0].from)} – ${date(ranges[ranges.length - 1].to)}` : "—";

/**
 * Панель режима правки внизу графика: сколько изменений, сколько операций не прошло,
 * «Отменить шаг», «Отменить всё», «Применить». Применение идёт через окно со списком
 * изменений — как «Review changes» в Jira Plans: видно, что уйдёт в план, а если за
 * время правки план меняли другие, там же — их правки и выбор «применить поверх».
 */
export const PlanDraftPanel: React.FC<PlanDraftPanelProps> = (props) => {
    const {
        busy,
        dirty,
        hasDraft,
        canUndo,
        projects,
        conflicts,
        preview,
        onRevealPush,
        onSave,
        onCancel,
        onUndo,
        onDiscard,
        onReview,
        onApply,
    } = props;

    /** Черновик для окна применения: считает сервер, он же и применит */
    const [draft, setDraft] = useState<PlanDraftView | null>(null);
    const review = !!draft;
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [result, setResult] = useState<PlanDraftApplyResult | null>(null);
    /** Идёт применение — окно закрыто лоадером, повторно нажать нельзя */
    const [applying, setApplying] = useState(false);

    const rejected = draft ? draft.operations.filter((operation) => !!operation.rejected) : [];

    const names = useMemo(() => {
        const employee = new Map<string, string>();
        const orders = new Map<string, ProjectPlanProject>();

        projects.forEach((project) => {
            orders.set(project.id, project);
            project.rows.forEach((row) => row.kind === "employer" && employee.set(row.id, row.label));
        });

        return { employee, orders };
    }, [projects]);

    const orderLabel = (orderId: string) => {
        const project = names.orders.get(orderId);
        return project ? `${project.fullInventoryNumber} ${project.name}` : "";
    };

    const sectionCode = (orderId: string, guid: string | null) => {
        const project = names.orders.get(orderId);
        const section = project && guid ? project.sections.find((item) => item.id === guid) : null;
        return section ? section.code || section.name : "";
    };

    /** «Применить черновик» доступен, только когда всё сохранено: применяется то, что на сервере */
    const openReview = useCallback(async () => {
        setResult(null);
        setDraft(await onReview());
    }, [onReview]);

    const closeReview = useCallback(() => setDraft(null), []);

    const apply = useCallback(
        async (force: boolean) => {
            setApplying(true);

            try {
                const applied = await onApply(force);

                if (applied && applied.applied) {
                    setDraft(null);
                    setResult(null);
                } else {
                    setResult(applied);
                }
            } finally {
                setApplying(false);
            }
        },
        [onApply]
    );

    const discrepancies = result ? result.discrepancies : [];

    /** Кого толкает (или должен толкнуть) каскад: «Иванов И.И. · КР», «Раздел КР» */
    const pushLabel = (push: PlanDraftPush) => {
        const section = push.contentGuid ? sectionCode(push.orderId, push.contentGuid) : "";

        if (push.kind === "section") {
            return `Раздел ${section}`.trim();
        }

        const employer = (push.employerId && names.employee.get(push.employerId)) || "Работа";
        return section ? `${employer} · ${section}` : employer;
    };

    // каскад по связям: что сдвинуто, сколько чужих проектов задето и что упёрлось в права
    const pushed = (preview && preview.pushed) || [];
    const blocked = (preview && preview.blocked) || [];
    const cascadeOrders = (preview && preview.cascadeOrders) || [];

    /** Строка списка: проект, кто сдвинут, на сколько; клик — к блоку на графике */
    const pushItem = (push: PlanDraftPush, isBlocked: boolean) => {
        const project = names.orders.get(push.orderId);

        return (
            <button
                key={`${isBlocked ? "blocked" : "pushed"}:${push.kind}:${push.assignmentId || push.contentGuid}:${push.linkId || ""}`}
                type="button"
                className={styles.pushItem}
                onClick={() => onRevealPush && onRevealPush(push)}
            >
                <span className={styles.pushOrder}>{project ? project.fullInventoryNumber : ""}</span>
                <span className={styles.pushName}>{pushLabel(push)}</span>
                <span className={isBlocked ? styles.pushBlocked : styles.pushShift}>
                    {isBlocked ? `+${push.shift} · на согласование` : `+${push.shift} раб. дн.`}
                </span>
            </button>
        );
    };

    const cascadeNote = (pushed.length > 0 || blocked.length > 0) && (
        <Popover
            interactionKind="hover"
            hoverOpenDelay={150}
            hoverCloseDelay={250}
            placement="top-start"
            minimal={true}
            content={
                <div className={styles.pushList}>
                    {pushed.length > 0 && <div className={styles.pushCaption}>Сдвинуто по связям</div>}
                    {pushed.map((push) => pushItem(push, false))}
                    {blocked.length > 0 && (
                        <div className={styles.pushCaption}>Уйдёт на согласование ГИПу при применении</div>
                    )}
                    {blocked.map((push) => pushItem(push, true))}
                    <div className={styles.pushHint}>Клик — показать на графике</div>
                </div>
            }
        >
            <span className={styles.cascade}>
                {cascadeOrders.length > 0
                    ? `Затронуто в других проектах: ${cascadeOrders.length}`
                    : `Сдвинуто по связям: ${pushed.length}`}
                {blocked.length > 0 && ` · требует согласования: ${blocked.length}`}
            </span>
        </Popover>
    );

    return (
        <>
            <PlanSubmitButtons
                disabled={busy}
                nothingToSave={!dirty}
                hideUnavailable={true}
                // либо правки на странице (сохранить / отменить), либо сохранённый черновик
                // (применить / удалить) — два набора кнопок не показываются вместе
                canUndo={dirty && canUndo}
                onSubmit={onSave}
                onCancel={onCancel}
                onUndo={onUndo}
                extra={
                    <>
                        {cascadeNote}
                        {!dirty && hasDraft && (
                            <>
                                <PlanrButton
                                    type="greenish"
                                    size="small"
                                    className={styles.apply}
                                    disabled={busy}
                                    onClick={openReview}
                                    title="Перенести черновик в план: дни сотрудников, вехи и сроки"
                                >
                                    Применить черновик
                                </PlanrButton>
                                <PlanrButton
                                    type="lightdanger"
                                    size="small"
                                    disabled={busy}
                                    onClick={() => setConfirmDiscard(true)}
                                >
                                    Удалить черновик
                                </PlanrButton>
                            </>
                        )}
                    </>
                }
            />

            <Dialog
                isOpen={confirmDiscard}
                onClose={() => setConfirmDiscard(false)}
                title="Удалить черновик?"
                className="figma-dialog"
            >
                <div className={Classes.DIALOG_BODY}>
                    Сохранённые и несохранённые правки графика будут удалены, план останется как был.
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <PlanrButton type="secondary" size="small" onClick={() => setConfirmDiscard(false)}>
                            Вернуться
                        </PlanrButton>
                        <PlanrButton
                            type="danger"
                            size="small"
                            disabled={busy}
                            onClick={() => {
                                setConfirmDiscard(false);
                                onDiscard();
                            }}
                        >
                            Удалить черновик
                        </PlanrButton>
                    </div>
                </div>
            </Dialog>

            <Dialog
                isOpen={review}
                onClose={() => !applying && closeReview()}
                title="Изменения черновика"
                className={`figma-dialog ${styles.dialog}`}
                canEscapeKeyClose={!applying}
                canOutsideClickClose={!applying}
                isCloseButtonShown={!applying}
            >
                <LocalLoader active={applying} text="Применяем изменения…" />

                <div className={`${Classes.DIALOG_BODY} ${styles.body}`}>
                    {discrepancies.length > 0 && (
                        <div className={styles.discrepancies}>
                            <div className={styles.caption}>
                                Пока вы правили, план поменяли другие. Применить поверх их правок?
                            </div>
                            {discrepancies.map((item, index) => (
                                <div key={index} className={styles.line}>
                                    <span>{item.title}</span>
                                    <span className={styles.meta}>
                                        {item.modifiedBy} · {moment(item.modified).format("DD.MM.YYYY HH:mm")}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* конфликты не блокируют применение (п. 7 ТЗ) — только предупреждают */}
                    {conflicts && conflicts.length > 0 && (
                        <div className={styles.conflicts}>
                            <div className={styles.caption}>
                                В плане остаются непогашенные конфликты: {conflicts.length}. Применить можно, но
                                проверьте их в панели «Конфликты».
                            </div>
                            {conflicts.slice(0, CONFLICTS_SHOWN).map((conflict) => (
                                <div key={conflict.id} className={styles.conflictLine}>
                                    {conflict.title}
                                </div>
                            ))}
                            {conflicts.length > CONFLICTS_SHOWN && (
                                <div className={styles.conflictLine}>…и ещё {conflicts.length - CONFLICTS_SHOWN}</div>
                            )}
                        </div>
                    )}

                    {draft &&
                        draft.works.map((work) => (
                            <div key={work.id} className={styles.line}>
                                <span>
                                    {names.employee.get(work.employerId) || "Сотрудник"}
                                    {work.contentGuid && ` · ${sectionCode(work.orderId, work.contentGuid)}`}
                                    <span className={styles.meta}> {orderLabel(work.orderId)}</span>
                                </span>
                                <span className={styles.change}>
                                    {period(work.before)} → {period(work.after)}
                                    {!!work.pushed && (
                                        <span className={styles.pushed} data-tip="Сдвинуто каскадом по связям">
                                            {" "}
                                            каскад +{work.pushed}
                                        </span>
                                    )}
                                </span>
                            </div>
                        ))}

                    {draft &&
                        draft.milestones.map((milestone) => {
                            const project = names.orders.get(milestone.orderId);
                            const item = project ? project.milestones.find((m) => m.id === milestone.id) : null;

                            return (
                                <div key={milestone.id} className={styles.line}>
                                    <span>
                                        Веха «{item ? item.name : ""}»
                                        <span className={styles.meta}> {orderLabel(milestone.orderId)}</span>
                                    </span>
                                    <span className={styles.change}>
                                        {date(milestone.before)} → {date(milestone.after)}
                                    </span>
                                </div>
                            );
                        })}

                    {draft &&
                        draft.terms.map((term) => (
                            <div key={`${term.orderId}/${term.contentGuid || ""}`} className={styles.line}>
                                <span>
                                    {term.contentGuid
                                        ? `Срок раздела ${sectionCode(term.orderId, term.contentGuid)}`
                                        : "Плановый срок проекта"}
                                    <span className={styles.meta}> {orderLabel(term.orderId)}</span>
                                </span>
                                <span className={styles.change}>
                                    {term.beforeEnd
                                        ? term.contentGuid
                                            ? `${date(term.beforeStart)} – ${date(term.beforeEnd)}`
                                            : date(term.beforeEnd)
                                        : term.contentGuid
                                          ? "по работам"
                                          : "не задан"}{" "}
                                    →{" "}
                                    {term.contentGuid
                                        ? `${date(term.afterStart)} – ${date(term.afterEnd)}`
                                        : date(term.afterEnd)}
                                </span>
                            </div>
                        ))}

                    {draft && (draft.cascadeOrders || []).length > 0 && (
                        <div className={styles.cascadeList}>
                            <div className={styles.caption}>Затронуто в других проектах по связям</div>
                            {(draft.cascadeOrders || []).map((orderId) => (
                                <div key={orderId} className={styles.conflictLine}>
                                    {orderLabel(orderId) || "Проект вне графика"}
                                </div>
                            ))}
                        </div>
                    )}

                    {draft && (draft.blocked || []).length > 0 && (
                        <div className={styles.conflicts}>
                            <div className={styles.caption}>
                                Уйдёт на согласование ГИПам — сдвинется после их решения:
                            </div>
                            {(draft.blocked || []).map((push) => (
                                <div key={push.linkId || pushLabel(push)} className={styles.conflictLine}>
                                    {pushLabel(push)}
                                    <span className={styles.meta}> {orderLabel(push.orderId)}</span> · +{push.shift}{" "}
                                    раб. дн.
                                    {push.gipName && <span className={styles.meta}> · ГИП {push.gipName}</span>}
                                </div>
                            ))}
                        </div>
                    )}

                    {rejected.length > 0 && (
                        <div className={styles.rejectedList}>
                            Не применятся: {rejected.map((operation) => operation.rejected).join("; ")}
                        </div>
                    )}
                </div>

                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <PlanrButton type="secondary" size="small" disabled={applying} onClick={closeReview}>
                            Вернуться к правке
                        </PlanrButton>
                        <PlanrButton
                            type={discrepancies.length ? "danger" : "greenish"}
                            size="small"
                            disabled={busy || applying}
                            onClick={() => apply(discrepancies.length > 0)}
                        >
                            {discrepancies.length ? "Применить поверх" : "Применить"}
                        </PlanrButton>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
